import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, type UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {type AxiosError} from 'axios'

import {riskSurveyMutations} from '../../api'
import type {AddReportLinkRequest, ReportLinkDto} from '../../types'
import {useRiskSurveyTranslation} from '../useRiskSurveyTranslation'

import {riskSurveyQueryKeys} from './riskSurveyQueryKeys'

type Variables = {
  reportId: string
  data: AddReportLinkRequest
}

export const useAddReportLink = (
  options?: UseMutationOptions<ReportLinkDto, AxiosError, Variables>
) => {
  const {t} = useRiskSurveyTranslation()
  const queryClient = useQueryClient()

  return useMutation<ReportLinkDto, AxiosError, Variables>({
    mutationFn: ({reportId, data}) => riskSurveyMutations.addReportLink(reportId, data),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      spawnSuccessToast(t('links.addSuccess'))
      void options?.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      spawnErrorToast(t('links.addError'))
      void options?.onError?.(error, variables, onMutateResult, context)
    },
    onSettled: (data, error, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({queryKey: riskSurveyQueryKeys.reports.lists()})
      void queryClient.invalidateQueries({
        queryKey: riskSurveyQueryKeys.reports.detail(variables.reportId)
      })
      void queryClient.invalidateQueries({
        queryKey: riskSurveyQueryKeys.reports.links(variables.reportId)
      })
      void options?.onSettled?.(data, error, variables, onMutateResult, context)
    }
  })
}
